import React, { useMemo, useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { DatePickerComponent } from "@syncfusion/ej2-react-calendars";

const safe4DDate = (v) => {
    if (!v || v === "0000-00-00") return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};

const getSiteDate = (s) =>
    safe4DDate(s?.Date_PVRecep_String);

const getDeclarationDate = (d) =>
    safe4DDate(d?.Date_Declaration);

const getDommageDate = (d) =>
    safe4DDate(d?.Date_PVRecep);

const getExpertiseDate = (e) =>
    safe4DDate(e?.Date_Rdv) || safe4DDate(e?.Date_Convoc) || safe4DDate(e?.Date_evenement);

const buildYearsRange = (fromYear, toYear) => {
    const years = [];
    for (let y = fromYear; y <= toYear; y += 1) years.push(y);
    return years;
};

export default function GlobalTimelineApex({
    sites = [],
    declarations = [],
    dommages = [],
    expertises = [],
    height = 360,
}) {

    const declarationDateByUUID = useMemo(() => {
        const m = new Map();

        declarations.forEach((dec) => {
            const uuid = dec?.UUID_Declaration_;
            const dt = getDeclarationDate(dec);
            if (uuid && dt) m.set(String(uuid), dt);
        });

        return m;
    }, [declarations]);

    const getDommageDateFromDeclaration = (dom) => {
        const uuidDecl = dom?.UUID_Declaration_;
        if (!uuidDecl) return null;
        return declarationDateByUUID.get(String(uuidDecl)) || null;
    };

    const minDataYear = useMemo(() => {
        const years = [];

        sites.forEach((s) => {
            const d = getSiteDate(s);
            if (d) years.push(d.getFullYear());
        });
        declarations.forEach((d) => {
            const dd = getDeclarationDate(d);
            if (dd) years.push(dd.getFullYear());
        });
        dommages.forEach((d) => {
            const dd = getDommageDateFromDeclaration(d);
            if (dd) years.push(dd.getFullYear());
        });
        expertises.forEach((e) => {
            const dd = getExpertiseDate(e);
            if (dd) years.push(dd.getFullYear());
        });

        if (!years.length) return null;
        return Math.min(...years);
    }, [sites, declarations, dommages, expertises]);

    const currentYear = new Date().getFullYear();

    const [fromYear, setFromYear] = useState(currentYear - 10);
    const [toYear, setToYear] = useState(currentYear);

    useEffect(() => {
        if (minDataYear && fromYear === currentYear) setFromYear(minDataYear);
    }, [minDataYear]);

    const fromYearDate = new Date(fromYear, 0, 1);
    const toYearDate = new Date(toYear, 0, 1);

    const { categories, series } = useMemo(() => {

        if (!minDataYear) {
            return {
                categories: [],
                series: [
                    { name: "Sites", data: [] },
                    { name: "Déclarations", data: [] },
                    { name: "Dommages", data: [] },
                    { name: "Expertises", data: [] },
                ],
            };
        }

        const startY = Math.min(fromYear, toYear);
        const endY = Math.max(fromYear, toYear);
        const years = buildYearsRange(startY, endY);

        const buckets = Object.fromEntries(
            years.map((y) => [y, { sites: 0, declas: 0, dommages: 0, expertises: 0 }])
        );

        const inRange = (y) => y >= startY && y <= endY;

        sites.forEach((s) => {
            const d = getSiteDate(s);
            if (!d) return;
            const y = d.getFullYear();
            if (inRange(y)) buckets[y].sites += 1;
        });

        declarations.forEach((d) => {
            const dd = getDeclarationDate(d);
            if (!dd) return;
            const y = dd.getFullYear();
            if (inRange(y)) buckets[y].declas += 1;
        });

        dommages.forEach((d) => {
            const dd = getDommageDate(d);
            if (!dd) return;
            const y = dd.getFullYear();
            if (inRange(y)) buckets[y].dommages += 1;
        });

        expertises.forEach((e) => {
            const dd = getExpertiseDate(e);
            if (!dd) return;
            const y = dd.getFullYear();
            if (inRange(y)) buckets[y].expertises += 1;
        });

        return {
            categories: years.map(String),
            series: [
                { name: "Sites", data: years.map((y) => buckets[y].sites) },
                { name: "Déclarations", data: years.map((y) => buckets[y].declas) },
                { name: "Dommages", data: years.map((y) => buckets[y].dommages) },
                { name: "Expertises", data: years.map((y) => buckets[y].expertises) },
            ],
        };
    }, [sites, declarations, dommages, expertises, minDataYear, fromYear, toYear]);

    const options = useMemo(
        () => ({
            chart: {
                type: "area",
                toolbar: { show: false },
                zoom: { enabled: false },
                fontFamily: "inherit",
            },
            colors: ["#f97316", "#0ea5e9", "#a855f7", "#22c55e"],
            dataLabels: { enabled: false },
            stroke: { curve: "smooth", width: 3 },
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 0.6,
                    opacityFrom: 0.25,
                    opacityTo: 0.02,
                    stops: [0, 90, 100],
                },
            },
            grid: {
                borderColor: "rgba(249, 115, 22, 0.15)",
                strokeDashArray: 4,
            },
            xaxis: {
                type: "category",
                categories,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: { style: { fontSize: "12px" } },
            },
            yaxis: {
                min: 0,
                forceNiceScale: true,
                labels: { formatter: (v) => Math.round(v) },
            },
            legend: {
                position: "top",
                horizontalAlign: "left",
                markers: { radius: 12 },
            },
            tooltip: {
                x: { formatter: (v) => `Année ${v}` },
            },
        }),
        [categories]
    );

    return (
        <div className="relative bg-white p-5 border border-orange-100 rounded-2xl overflow-hidden">
            <div className="top-0 absolute inset-x-0 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 h-1" />

            <div className="flex lg:flex-row flex-col lg:justify-between lg:items-start gap-4">
                <div>
                    <div className="font-bold text-orange-600 text-sm uppercase tracking-wide">
                        Évolution globale (par année)
                    </div>
                    <div className="mt-1 text-gray-500 text-xs">
                        Sites / Déclarations / Dommages / Expertises
                    </div>
                </div>

                {/* ✅ DatePickers année-only */}
                <div className="flex sm:flex-row flex-col gap-3">
                    <div className="px-3 py-2 border border-orange-100 rounded-xl">
                        <div className="font-semibold text-[11px] text-orange-600 uppercase tracking-widest">
                            Du
                        </div>
                        <DatePickerComponent
                            format="yyyy"
                            start="Decade"
                            depth="Decade"
                            value={fromYearDate}
                            change={(e) => {
                                if (!e.value) return;
                                const y = e.value.getFullYear();
                                if (y > toYear) setToYear(y);
                                setFromYear(y);
                            }}
                        />
                    </div>

                    <div className="px-3 py-2 border border-orange-100 rounded-xl">
                        <div className="font-semibold text-[11px] text-orange-600 uppercase tracking-widest">
                            Au
                        </div>
                        <DatePickerComponent
                            format="yyyy"
                            start="Decade"
                            depth="Decade"
                            value={toYearDate}
                            change={(e) => {
                                if (!e.value) return;
                                const y = e.value.getFullYear();
                                // si to < from, on baisse from
                                if (y < fromYear) setFromYear(y);
                                setToYear(y);
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-4">
                <ReactApexChart options={options} series={series} type="area" height={height} />
            </div>
        </div>
    );
}
